import { useCallback, useEffect, useRef, useState } from "react";
import { bool, func, string } from "prop-types";
import styled from "styled-components/macro";
import { rem } from "polished";

import { BREAKPOINT_WRAPPER, routes } from "../../constants";
import { colors } from "../../styles/Styles.variables";
import history from "../../helpers/history";

import Button from "../../styles/components/Button";
import ButtonLink from "../../styles/components/ButtonLink";
import Form from "../form";
import Group from "../form/components/Group";
import Label from "../form/components/Label";
import Modal, { Body } from "../modal";
import Text from "../form/components/Text";

import bgJpg from "../../assets/img/backgrounds/modal-signup-bg.jpg";
import bgJpgX2 from "../../assets/img/backgrounds/modal-signup-bg@2x.jpg";
import bgWebp from "../../assets/img/backgrounds/modal-signup-bg.webp";
import bgWebpX2 from "../../assets/img/backgrounds/modal-signup-bg@2x.webp";

function ModalSignup({
  handleClose,
  handleOpen,
  open,
  targetDevice,
  trackEvent,
  trackModalView,
  useWebP,
}) {
  const [email, setEmail] = useState("");

  const handleInput = useCallback(e => {
    setEmail(e.target.value);
  }, []);

  const handleReject = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      handleClose(true, email);
      if (trackEvent) {
        trackEvent({
          category: "Form",
          action: "Submit",
          label: "Weekly Used Equipment Email Sign-Up Modal",
        });
      }
      history.push(routes.emailSignup.path);
    },
    [email, handleClose, trackEvent]
  );

  // Show modal after a delay
  useEffect(() => {
    const timer = setTimeout(() => {
      handleOpen();
      if (trackModalView) {
        trackModalView("signup");
      }
    }, 30 * 1000);

    return () => clearTimeout(timer);
  }, [handleOpen, trackModalView]);

  // Focus on the email input when the modal opens
  const emailInputRef = useRef(null);
  useEffect(() => {
    if (open && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [open]);

  return (
    <ModalStyled
      aria-label="Sign up for weekly newsletter"
      close={handleReject}
      open={open}
      showClose={targetDevice === "mobile"}
      useWebP={useWebP}
    >
      <header>
        <h1>
          <strong>Don’t Miss Out!</strong>
        </h1>
        <p className="mb-0">
          Get the <strong>hottest deals</strong> on the latest machines with our
          weekly used equipment newsletter.
        </p>
      </header>
      <Form
        onSubmit={handleSubmit}
        theme={targetDevice === "mobile" ? "dark" : "light"}
      >
        <div className="form-fields">
          <Group>
            <Label htmlFor="email" required>
              Email
            </Label>
            <Text
              id="email"
              inputRef={emailInputRef}
              maxLength="200"
              onChange={handleInput}
              required
              type="email"
              value={email}
            />
          </Group>
        </div>
        <div className="form-actions">
          <ButtonStyled
            block={targetDevice === "mobile"}
            color="blue500"
            size={targetDevice === "mobile" ? "small" : "medium"}
            square={targetDevice === "mobile"}
            type="submit"
          >
            Sign me up!
          </ButtonStyled>
          {targetDevice !== "mobile" && (
            <ButtonLinkStyled color="white" onClick={handleReject} underline>
              No thanks
            </ButtonLinkStyled>
          )}
        </div>
      </Form>
    </ModalStyled>
  );
}

function getBgImage({ useWebP }) {
  const bg = useWebP ? bgWebp : bgJpg;
  const bgX2 = useWebP ? bgWebpX2 : bgJpgX2;

  return `image-set(url(${bg}) 1x, url(${bgX2}) 2x)`;
}

const ButtonLinkStyled = styled(ButtonLink)`
  font-size: ${rem("17px")};
`;

const ButtonStyled = styled(Button)`
  @media ${BREAKPOINT_WRAPPER} {
    border-radius: ${rem("24px")};
  }
`;

const ModalStyled = styled(Modal)`
  ${Body} {
    header {
      h1 {
        text-transform: uppercase;
      }

      p strong {
        color: ${colors.yellow};
      }
    }
  }

  @media ${BREAKPOINT_WRAPPER} {
    ${Body} {
      background-image: ${getBgImage};
      background-repeat: no-repeat;
      background-position: center top;
      background-size: cover;
      border: ${rem("12px")} solid ${colors.white};
      border-radius: 0;
      max-width: ${rem("684px")};
      padding: ${rem("82px")} ${rem("76px")};
    }

    header {
      text-align: center;

      &:not(.alert-only) {
        padding-bottom: ${rem("32px")};
      }

      h1 {
        font-size: ${rem("40px")};
        line-height: 1.2;
        margin-bottom: ${rem("8px")};
      }

      p {
        font-size: ${rem("20px")};
        line-height: 1.3;
        text-wrap: balance;
      }
    }

    .form-actions {
      align-items: center;
      display: flex;
      gap: ${rem("16px")};
      flex-direction: column;
      justify-content: center;
      margin-top: ${rem("24px")};
    }
  }
`;

ModalSignup.propTypes = {
  open: bool,
  handleClose: func.isRequired,
  handleOpen: func.isRequired,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  useWebP: bool,
};

export default ModalSignup;
